import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useIdentityChallengeContext } from 'Context/IdentityChallengeContext';

const SensitiveInfoInputContext = React.createContext(undefined);

// Actions
const SET_SENSITIVE_INFO_COLLECTION = 'set-sensitive-info-collection';
const SET_LOADING = 'set-loading';
const UNLOCK_INPUT = 'unlock-input';
const SET_SAVING = 'set-saving';

const init = () => {
  return {
    fieldValue: null,
    isLocked: true,
    sensitiveInfoCollection: null,
    isLoading: false,
    isSaving: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SET_SENSITIVE_INFO_COLLECTION: {
      return {
        ...state,
        isLocked: false,
        isLoading: false,
        isSaving: false,
        sensitiveInfoCollection: action.sensitiveInfoCollection,
      };
    }
    case UNLOCK_INPUT: {
      return {
        ...state,
        isLocked: false,
      };
    }
    case SET_LOADING: {
      return {
        ...state,
        isLoading: action.isLoading,
      };
    }
    case SET_SAVING: {
      return {
        ...state,
        isSaving: action.isSaving,
      };
    }
    default: {
      throw new Error(`Unhandled SensitiveInfoInputContext action type: ${action.type}`);
    }
  }
};

const SensitiveInfoInputProvider = ({
  children,
  resource,
  relationshipName,
  writeOnly,
  fieldName,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { handleInterceptResponse } = useIdentityChallengeContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  const unlockInput = () => {
    dispatch({ type: UNLOCK_INPUT });
  };

  const fetchSensitiveInfoCollection = React.useCallback(async () => {
    try {
      dispatch({ type: SET_LOADING, isLoading: true });

      const maybeColl = await resource.relationship(relationshipName).link('related').get();
      const interceptResponseColl = await handleInterceptResponse(maybeColl, (coll) => {
        return coll;
      }, (err) => {
        displayErrorMessage(err);
      });

      dispatch({
        type: SET_SENSITIVE_INFO_COLLECTION,
        sensitiveInfoCollection: interceptResponseColl,
      });
    } catch (err) {
      displayErrorMessage(err, err.errors);
      dispatch({ type: SET_LOADING, isLoading: false });
    }
  }, []);

  const submitSensitiveInfoResource = React.useCallback(async (value) => {
    try {
      dispatch({ type: SET_SAVING, isSaving: true });

      const [siResource] = state.sensitiveInfoCollection;
      const maybeColl = await siResource.attr(fieldName, value).submit();
      const interceptResponseColl = await handleInterceptResponse(maybeColl, (coll) => {
        return coll;
      }, (err) => {
        displayErrorMessage(err);
      });

      displaySuccess('Save successful');
      dispatch({
        type: SET_SENSITIVE_INFO_COLLECTION,
        sensitiveInfoCollection: interceptResponseColl,
      });
    } catch (err) {
      displayErrorMessage(err, err.errors);
      dispatch({ type: SET_SAVING, isSaving: false });
    }
  }, [state, fieldName]);

  const memoizedValue = React.useMemo(() => {
    const fieldValue = state.sensitiveInfoCollection?.resources().head()?.attr(fieldName);
    return {
      unlockInput,
      isLocked: state.isLocked,
      writeOnly,
      isLoading: state.isLoading,
      sensitiveInfoCollection: state.sensitiveInfoCollection,
      fieldValue,
      fetchSensitiveInfoCollection,
      submitSensitiveInfoResource,
      resource,
      relationshipName,
    };
  }, [
    writeOnly,
    state,
    unlockInput,
    fetchSensitiveInfoCollection,
    submitSensitiveInfoResource,
    resource,
    relationshipName,
  ]);

  return (
    <SensitiveInfoInputContext.Provider value={memoizedValue}>
      {children}
    </SensitiveInfoInputContext.Provider>
  );
};

// Use SensitiveInfoInput State Context
const useSensitiveInfoInputContext = () => {
  const context = React.useContext(SensitiveInfoInputContext);
  if (context === undefined) {
    throw new Error('useSensitiveInfoInputContext must be used within a SensitiveInfoInputProvider');
  }
  return context;
};

SensitiveInfoInputProvider.defaultProps = {
  fieldName: 'value',
  writeOnly: null,
};

SensitiveInfoInputProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  resource: PropTypes.object.isRequired,
  writeOnly: PropTypes.bool,
  relationshipName: PropTypes.string.isRequired,
  fieldName: PropTypes.string,
};

export {
  SensitiveInfoInputProvider,
  useSensitiveInfoInputContext,
};
